import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { PhoneNav } from "../../../../shared/phone/PhoneNav";
import { PnRow } from "../../../../shared/phone/Row";
import { Sheet } from "../../../../shared/phone/Sheet";
import { refreshBadges } from "../../../components/phone/badges";
import { Icon } from "../../../components/ui/Icon";
import { ccApi, type Need, type ReadyLite, type ReadyStep } from "../../../lib/ccApi";
import { goalsApi, type GoalView } from "../../../lib/goalsApi";
import { initials } from "../../goal/text";
import { ago, needDiff } from "../Needs";
import { fmt, needText, shortGoal } from "../text";
import { errText, useHome } from "../useCc";
import { closeSheet, ProductMark, waitIcon } from "./parts";
import type { Waiting } from "./waiting";
import { useCan, useMayDecide } from "../../../context/AuthContext";

/**
 * One thing to decide, on a phone (a tall sheet over the list) and on an iPad (the pane beside the list):
 * what it is in full, the checks it was held against, and the decision, named for what it does. After a
 * decision the next one comes in, until none is left.
 */

const str = (v: unknown) => (typeof v === "string" ? v : "");
const goalPath = (id: string) => `/goals/${encodeURIComponent(id)}`;
const peopleIn = (n: Need) => (Array.isArray(n.payload.people) ? n.payload.people.length : (n.context?.people?.length ?? 0));
const peopleWords = (k: number) => (k === 1 ? "1 person" : `${fmt(k)} people`);

// --- What a need's screen reads besides the need ---------------------------------------------------------

interface NeedData {
  loaded: boolean;
  ready: ReadyLite | null;
  view: GoalView | null;
  steps: ReadyStep[] | null;
}

const NONE: NeedData = { loaded: false, ready: null, view: null, steps: null };

/** The ad and email words, the goal's settings or its plan, or its steps before launch, as the kind needs. */
function useNeedData(w: Waiting | null): NeedData {
  const [got, setGot] = useState<{ key: string; d: NeedData } | null>(null);
  const key = w?.key ?? "";
  const goalId = w?.goal.id ?? "";
  const kind = w ? (w.need?.kind ?? w.kind) : "";
  useEffect(() => {
    if (!key) return;
    let live = true;
    const wantsReady = kind === "ad.add" || kind === "ad.text" || kind === "email.step";
    const wantsView = kind === "plan" || kind === "goal.change" || kind === "audience.update" || kind === "placements.set";
    void Promise.all([
      wantsReady ? ccApi.ready(goalId).catch(() => null) : null,
      wantsView ? goalsApi.get(goalId).catch(() => null) : null,
      kind === "ready" ? ccApi.readySteps(goalId).then((r) => r.steps, () => null) : null,
    ]).then(([ready, view, steps]) => {
      if (live) setGot({ key, d: { loaded: true, ready, view, steps } });
    });
    return () => {
      live = false;
    };
  }, [key, goalId, kind]);
  return got?.key === key ? got.d : NONE;
}

// --- Its words ----------------------------------------------------------------------------------------

/** The sheet's title: short, the way the phone names what is open. */
function titleOf(w: Waiting): string {
  if (w.kind === "plan") return "The plan";
  if (w.kind === "ready") return "Getting ready";
  const n = w.need as Need;
  if (n.kind === "email.reply") return n.context?.person ? `Reply to ${n.context.person.name.split(" ")[0]}` : "Reply";
  if (n.kind === "ad.add") return "New ad to test";
  if (n.kind === "email.step") return `Send email ${Number(n.payload.step) || 1}`;
  return needText(n).kind;
}

/** The decision's button, named for what it does. */
function mainLabel(n: Need): string {
  switch (n.kind) {
    case "email.reply":
      return "Send reply";
    case "ad.add":
      return "Add to the ad set";
    case "email.step":
      return `Send to ${peopleWords(peopleIn(n))}`;
    case "goal.change":
      return "Apply the change";
    case "budget.set":
      return "Change the budget";
    case "audience.update":
    case "placements.set":
      return "Make the change";
    case "campaign.create":
      return "Create the campaign";
    case "campaign.turn_on":
      return "Turn the ads on";
    case "ad.text":
      return "Use the new text";
    case "ad.pause":
      return "Pause the ad";
    case "email.lead_pause":
      return "Stop emailing them";
    case "email.pause":
      return "Pause the emails";
    case "email.resume":
      return "Resume the emails";
    case "email.list":
      return "Approve the list";
    default:
      return "Approve";
  }
}

/** What the toast says once a decision ran at once. */
function doneWords(n: Need, result: Record<string, unknown> | null): string {
  if (n.kind === "email.reply") return "Reply sent";
  if (n.kind === "ad.add") return "Added to the ad set";
  if (n.kind === "email.step") return `Sending email ${Number(n.payload.step) || 1} to ${peopleWords(peopleIn(n))}`;
  if (typeof result?.version === "number") return `Changed. Plan version ${result.version}.`;
  return "Done";
}

// --- Its body, by kind --------------------------------------------------------------------------------

/** Text with the email's fill-ins ({first name}) shown as the words each person gets in their place. */
function withFills(line: string): ReactNode {
  return line.split(/(\{[^}]+\})/).map((x, i) =>
    /^\{[^}]+\}$/.test(x) ? (
      <span className="hm-var" key={i}>
        {x.slice(1, -1) === "first line" ? "a line about them" : x.slice(1, -1)}
      </span>
    ) : (
      x
    ),
  );
}

function ReplyBody({ n }: { n: Need }) {
  const c = n.context ?? {};
  return (
    <>
      {c.inbound?.body && (
        <div className="hm-msg">
          <i className="hm-face">{initials(c.person?.name ?? "?")}</i>
          <div>
            <div className="hm-who">
              <b>{c.person?.name ?? "They wrote"}</b>
              {c.inbound.at && <span>{ago(c.inbound.at)}</span>}
            </div>
            <div className="hm-bub">{c.inbound.body}</div>
          </div>
        </div>
      )}
      <div>
        <div className="pn-cap">Reply the agent wrote, as you</div>
        <div className="pn-card hm-draft">
          {str(n.payload.body)
            .split("\n")
            .map((l, i) => (l.trim() ? <p key={i}>{l}</p> : <br key={i} />))}
        </div>
        <p className="pn-note">It goes from your sending address, in the same thread.</p>
      </div>
    </>
  );
}

function AdBody({ n, goalId, ready, go }: { n: Need; goalId: string; ready: ReadyLite | null; go: (to: string) => void }) {
  const p = n.payload;
  const ad = ready?.ads.find((a) => a.id === p.adId) ?? null;
  const w = ad?.words ?? {};
  const primary = n.kind === "ad.text" ? str(p.primary) : str(w.primary);
  const headline = n.kind === "ad.text" ? str(p.headline) : str(w.headline);
  const media = ad?.asset ? `/api/goals/${encodeURIComponent(goalId)}/ads/${encodeURIComponent(ad.id)}/video` : null;
  const type = typeof ad?.asset?.contentType === "string" ? ad.asset.contentType : "";
  return (
    <>
      <div className="pn-card hm-ad">
        <span className="hm-poster">
          {media && type.startsWith("image/") ? <img src={media} alt="" /> : media && type.startsWith("video/") ? <video src={`${media}#t=0.1`} muted playsInline preload="metadata" /> : <Icon name="film" />}
        </span>
        <span className="hm-adt">
          <b>{headline || n.context?.ad?.name || str(w.name)}</b>
          {primary && <span>{primary}</span>}
        </span>
      </div>
      {ad?.status === "needs_video" && (
        <div className="pn-grp">
          <PnRow onClick={() => go(`${goalPath(goalId)}/ready`)} icon={<Icon name="upload" />} iconTone="warn" title="Add the video first" sub="It needs the video before it can run." chevron />
        </div>
      )}
    </>
  );
}

function MailBody({ n, ready }: { n: Need; ready: ReadyLite | null }) {
  const step = Number(n.payload.step) || 1;
  const mail = ready?.emails.find((e) => e.step === step) ?? null;
  const list = n.context?.people ?? [];
  const k = peopleIn(n);
  const market = str(n.payload.market);
  return (
    <>
      {mail && (
        <div className="pn-card hm-mail">
          <div className="hm-mailh">
            <span>Email {step}</span>
            <span>{mail.when}</span>
          </div>
          <b>{mail.subject}</b>
          {mail.body
            .split("\n")
            .filter((l) => l.trim())
            .map((l, i) => (
              <p key={i}>{withFills(l)}</p>
            ))}
        </div>
      )}
      <div>
        <div className="pn-cap">{market ? `${peopleWords(k)} in ${market}` : peopleWords(k)}</div>
        {list.length > 0 && (
          <div className="hm-faces">
            {list.slice(0, 6).map((x) => (
              <i className="hm-face" key={x.id} title={x.name}>
                {initials(x.name)}
              </i>
            ))}
            {list.length > 6 && <span>+{list.length - 6}</span>}
          </div>
        )}
      </div>
    </>
  );
}

function ChangeBody({ n, view }: { n: Need; view: GoalView | null }) {
  const lines = needDiff(n, view?.current?.config ?? null);
  if (!lines.length) return null;
  return (
    <div>
      <div className="pn-cap">What changes</div>
      <div className="pn-grp hm-diff">
        {lines.map((l, i) => (
          <div className={`hm-dl ${l.kind}`} key={i}>
            <em>{l.kind === "add" ? "New" : "Now"}</em>
            <span>{l.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanBody({ w, view, go }: { w: Waiting; view: GoalView | null; go: (to: string) => void }) {
  const prop = view?.proposal ?? null;
  if (!prop) return null;
  const unsent = view?.comments.filter((c) => !c.sent_at).length ?? 0;
  return (
    <>
      <div className="pn-card hm-plan">
        <span className="pn-pill violet">
          <i />
          Version {prop.version}
        </span>
        {prop.plan?.summary && <p>{prop.plan.summary}</p>}
      </div>
      <div className="pn-grp">
        <PnRow onClick={() => go(`${w.to}/plan`)} icon={<Icon name="doc" />} iconTone="violet" title="Read the full plan" chevron />
      </div>
      {!prop.madeFromCurrentSetup && <p className="pn-note">Your settings changed after this plan was made. Plan again to include them.</p>}
      {unsent > 0 && <p className="pn-note">{unsent === 1 ? "A comment waits" : `${unsent} comments wait`} to be sent with Ask for changes.</p>}
    </>
  );
}

function StepsBody({ steps }: { steps: ReadyStep[] | null }) {
  if (!steps?.length) return null;
  return (
    <div className="pn-grp">
      {steps.map((s) => (
        <PnRow key={s.id} icon={<Icon name={s.ok ? "check" : "clock"} />} iconTone={s.ok ? "good" : "warn"} title={s.label} sub={s.ok ? "Done" : (s.detail ?? undefined)} wrap />
      ))}
    </div>
  );
}

/** Everything about one thing to decide: what it is, in full, and what it was checked against. */
function NeedBody({ w, data, go }: { w: Waiting; data: NeedData; go: (to: string) => void }) {
  const n = w.need;
  const { icon, tone } = waitIcon(w);
  const why = n ? str(n.payload.reason) : "";
  const failed = (n?.check?.checks ?? []).filter((c) => !c.ok);
  const line = n ? needText(n).line : "";
  const plain = n && !["email.reply", "ad.add", "ad.text", "email.step", "goal.change", "budget.set", "audience.update", "placements.set"].includes(n.kind);
  return (
    <div className="pn-stack tight hm-nd">
      <div className="hm-nhead">
        <span className={tone ? `pn-ico ${tone}` : "pn-ico"}>
          <Icon name={icon} />
        </span>
        <div>
          <b>{w.title}</b>
          <span>
            <ProductMark p={w.product} size={16} />
            {shortGoal(w.goal.sentence)}
          </span>
        </div>
      </div>
      {n?.kind === "email.reply" && <ReplyBody n={n} />}
      {(n?.kind === "ad.add" || n?.kind === "ad.text") && <AdBody n={n} goalId={w.goal.id} ready={data.ready} go={go} />}
      {n?.kind === "email.step" && <MailBody n={n} ready={data.ready} />}
      {(n?.kind === "goal.change" || n?.kind === "budget.set" || n?.kind === "audience.update" || n?.kind === "placements.set") && <ChangeBody n={n} view={data.view} />}
      {plain && line && line !== why && <div className="pn-card hm-line">{line}</div>}
      {w.kind === "plan" && <PlanBody w={w} view={data.view} go={go} />}
      {w.kind === "ready" && <StepsBody steps={data.steps} />}
      {why && (
        <div>
          <div className="pn-cap">Why the agent asks</div>
          <div className="pn-card hm-line">{why}</div>
        </div>
      )}
      {failed.length > 0 && (
        <div>
          <div className="pn-cap">Checked against your rules</div>
          <div className="pn-grp">
            {failed.map((c) => (
              <PnRow key={c.id} icon={<Icon name="info" />} iconTone={c.hard ? "danger" : "warn"} title={c.text} wrap />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Deciding -----------------------------------------------------------------------------------------

export interface NeedFlowProps {
  /** Everything that waits, in the order it is stepped through. */
  rows: Waiting[];
  current: Waiting;
  /** Show another one in the same place. */
  open: (key: string) => void;
  /** Nothing is left: back to the list. */
  done: () => void;
  /** Open a page the decision leads to. */
  go: (to: string) => void;
  /** A decided one, hidden at once while the list reads again. */
  gone: (key: string) => void;
  say: (msg: string, ok?: boolean) => void;
}

function useDecide({ rows, current, open, done, gone, say }: NeedFlowProps) {
  const { refresh } = useHome();
  const [busy, setBusy] = useState(false);
  const moveOn = () => {
    gone(current.key);
    void refresh();
    refreshBadges();
    const at = rows.findIndex((r) => r.key === current.key);
    const next = rows.slice(at + 1).concat(rows.slice(0, Math.max(0, at)))[0];
    if (next) open(next.key);
    else done();
  };
  const decide = async (d: "approve" | "skip") => {
    const n = current.need;
    if (!n || busy) return;
    setBusy(true);
    try {
      const r = await ccApi.decide(n.id, d);
      if (d === "skip") say("Skipped. Nothing happens.");
      else if (r.state === "refused" || r.state === "failed") {
        const why = r.result && typeof r.result.error === "string" ? r.result.error : r.result && typeof r.result.message === "string" ? r.result.message : "";
        say(`It could not run.${why ? ` ${why}` : ""}`, false);
      } else if (r.state === "approved") say("Approved. It runs in a few minutes.");
      else say(doneWords(n, r.result));
      moveOn();
    } catch (e) {
      say(errText(e), false);
    } finally {
      setBusy(false);
    }
  };
  const approvePlan = async (view: GoalView) => {
    if (!view.proposal || busy) return;
    setBusy(true);
    try {
      await goalsApi.approve(current.goal.id, view.proposal.id);
      say(current.goal.version > 0 ? "Plan approved." : "Plan approved. Getting ready is next.");
      moveOn();
    } catch (e) {
      say(errText(e), false);
    } finally {
      setBusy(false);
    }
  };
  return { busy, decide, approvePlan };
}

/** The decision at the foot: the main action named for what it does, and Skip (Not right for a plan). */
function NeedFoot({ w, data, go, flow }: { w: Waiting; data: NeedData; go: (to: string) => void; flow: ReturnType<typeof useDecide> }) {
  const can = useCan();
  const mayDecide = useMayDecide();
  if (w.kind === "ready" || w.kind === "plan" ? !can("editor") : !mayDecide(w.need as Need)) return null;
  if (w.kind === "ready")
    return (
      <button type="button" className="pn-btn pri wide" onClick={() => go(`${goalPath(w.goal.id)}/ready`)}>
        Open Get ready
      </button>
    );
  if (w.kind === "plan") {
    const v = data.view;
    const blocked = data.loaded && (!v?.proposal || !v.proposal.madeFromCurrentSetup || v.comments.some((c) => !c.sent_at));
    return (
      <div className="pn-btns hm-btns long">
        <button type="button" className="pn-btn" onClick={() => go(w.to)}>
          Not right
        </button>
        {blocked ? (
          <button type="button" className="pn-btn pri" onClick={() => go(w.to)}>
            Review the plan
          </button>
        ) : (
          <button type="button" className="pn-btn pri" disabled={!data.loaded || flow.busy} onClick={() => v && void flow.approvePlan(v)}>
            Approve the plan
          </button>
        )}
      </div>
    );
  }
  const n = w.need as Need;
  const main = mainLabel(n);
  return (
    <div className={main.length > 12 ? "pn-btns hm-btns long" : "pn-btns hm-btns"}>
      <button type="button" className="pn-btn" disabled={flow.busy} onClick={() => void flow.decide("skip")}>
        Skip
      </button>
      <button type="button" className="pn-btn pri" disabled={flow.busy} onClick={() => void flow.decide("approve")}>
        {main}
      </button>
    </div>
  );
}

/** Keeps a new one's top in view: the next decision starts at its beginning. */
function useTopOnChange(key: string, scroller: string) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.closest(scroller)?.scrollTo({ top: 0 });
  }, [key, scroller]);
  return ref;
}

const ofWords = (rows: Waiting[], w: Waiting) => `${rows.findIndex((r) => r.key === w.key) + 1} of ${rows.length}`;

/** On a phone: the tall sheet, with Close at the top and the decision at the foot. */
export function NeedSheet(props: NeedFlowProps & { onClose: () => void }) {
  const { rows, current: w, go, onClose } = props;
  const data = useNeedData(w);
  const flow = useDecide(props);
  const top = useTopOnChange(w.key, ".pn-sheet-body");
  return (
    <Sheet
      open
      tall
      onClose={onClose}
      label={titleOf(w)}
      title={titleOf(w)}
      left={
        <button type="button" className="pn-tbtn" onClick={closeSheet}>
          Close
        </button>
      }
      right={<span className="hm-of">{ofWords(rows, w)}</span>}
      foot={<NeedFoot w={w} data={data} go={go} flow={flow} />}
    >
      <div ref={top}>
        <NeedBody w={w} data={data} go={go} />
      </div>
    </Sheet>
  );
}

/** On an iPad: the same, as the pane beside the list. */
export function NeedPane(props: NeedFlowProps) {
  const { rows, current: w, go } = props;
  const data = useNeedData(w);
  const flow = useDecide(props);
  const top = useTopOnChange(w.key, ".pn-pane");
  return (
    <div className="pn-screen hm-pane" ref={top}>
      <PhoneNav title={titleOf(w)} actions={<span className="hm-of">{ofWords(rows, w)}</span>} />
      <NeedBody w={w} data={data} go={go} />
      <div className="pn-foot">
        <NeedFoot w={w} data={data} go={go} flow={flow} />
      </div>
    </div>
  );
}

/** Nothing waits: what the pane or the list shows instead. */
export function NothingWaits() {
  return (
    <div className="pn-empty hm-empty">
      <Icon name="check" />
      <b>Nothing waits for you</b>
      The agent adds things here when it needs a yes from you.
    </div>
  );
}
